import { samanDoctorAvailabilityStatus } from '@/common/apis/services/booking/samanDoctorAvailabilityStatus';
import { search, useSearch } from '@/common/apis/services/search/search';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import Button from '@/common/components/atom/button';
import Card from '@/common/components/atom/card';
import ErrorBoundary from '@/common/components/layouts/errorBoundary';
import Skeleton from '@/common/components/atom/skeleton';
import Text from '@/common/components/atom/text/text';
import useCustomize from '@/common/hooks/useCustomize';
import { splunkInstance } from '@/common/services/splunk';
import RecommendCard from '@/modules/booking/components/recommend/card/card';
import { doctorSlugFromSearchHit, useRaviCardRate } from '@/modules/profile/hooks/useDoctorRateDetails';
import SearchCard from '@/modules/search/components/card/card';
import { useQueries } from '@tanstack/react-query';
import random from 'lodash/random';
import { useEffect, useMemo, useRef } from 'react';
import { LocalPrivateSpecialistsConfig, localPrivateSpecialistsTitle, searchFilters } from './config';
import { LocalSpecialistsEventType, LocalSpecialistsPlacement, buildLocalSpecialistsEvent } from './events';
import { onlineVisitFactorUrl } from '@/common/utils/onlineVisitFactorUrl';
import { formatSamanAvailabilityTime } from '@/modules/booking/functions/mapSamanAvailabilityToCenters';
import {
  assembleTiers,
  firstTurnLabel,
  hasIndexedInPersonTurn,
  rankLocalSpecialists,
  tierCandidates,
  uniqueByDoctorName,
} from './rankLocalSpecialists';
import { assemblePlan, planCandidates, planSlug, tierCounts } from './plan';

const searchPageUrl = (config: LocalPrivateSpecialistsConfig) => {
  const query = Object.entries(searchFilters(config))
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  return `/s/${config.city}/${config.groupSlug}/${query ? `?${query}` : ''}`;
};

const slugOf = (doctor: any) => doctorSlugFromSearchHit(doctor) ?? '';

/** «پاسخ: آنلاین و آماده مشاوره» of the search's online-visit action, as plain text. */
const onlineResponseLabel = (doctor: any): string => {
  const action = (doctor?.actions ?? []).find((item: any) => item?.title === 'ویزیت آنلاین');
  return (action?.top_title ?? '').replace(/<[^>]*>/g, '').trim();
};

/** Plan mode (config.plan): the line above the button tells which kind of suggestion it is. */
const planLine = (doctor: any, config?: LocalPrivateSpecialistsConfig): string => {
  if (doctor._mode === 'online') {
    const where =
      doctor._tier === 'national-online' ? 'از پزشکان برتر کشور' : config?.cityName ? `پزشک ${config.cityName}` : '';
    return ['ویزیت آنلاین', where].filter(Boolean).join(' · ');
  }
  const turn = doctor._slot ? `اولین نوبت: ${formatSamanAvailabilityTime(doctor._slot)}` : firstTurnLabel(doctor);
  const badge = doctor._tier === 'preferred' ? config?.plan?.preferred[doctor._group]?.badge : undefined;
  return [badge, turn].filter(Boolean).join(' · ');
};

/** The line above the button, per tier (config.tiers / config.plan); otherwise the first free in-person turn. */
export const suggestionLine = (doctor: any, config?: LocalPrivateSpecialistsConfig): string => {
  if (doctor?._mode) return planLine(doctor, config);
  if (doctor?._tier === 'online') return ['ویزیت آنلاین', onlineResponseLabel(doctor)].filter(Boolean).join(' · ');
  if (doctor?._tier === 'clinic') {
    const turn = doctor._clinicSlot ? `اولین نوبت: ${formatSamanAvailabilityTime(doctor._clinicSlot)}` : firstTurnLabel(doctor);
    return ['نوبت در همین کلینیک', turn].filter(Boolean).join(' · ');
  }
  return firstTurnLabel(doctor);
};

const isOnlineSuggestion = (doctor: any) => doctor?._tier === 'online' || doctor?._mode === 'online';

/** Where the booking button goes: online-visit doctors straight to their online invoice. */
const actionHref = (doctor: any): string => (isOnlineSuggestion(doctor) ? onlineVisitFactorUrl(doctor) : doctor.url);

/** Profile card heading for the doctor shown: plan tiers have their own; otherwise the config's title. */
export const suggestionTitle = (doctor: any, config: LocalPrivateSpecialistsConfig): string => {
  const plan = config.plan;
  if (plan && doctor?._tier === 'local-online') return plan.localOnline.title;
  if (plan && doctor?._tier === 'national-online') return plan.nationalOnline.title;
  if (plan && doctor?._tier === 'preferred') return plan.preferred[doctor._group]?.title ?? localPrivateSpecialistsTitle(config);
  return localPrivateSpecialistsTitle(config);
};

/** «مشاهده سایر …» under the profile card: online plan tiers open the online-visit search. */
const moreLink = (doctor: any, config: LocalPrivateSpecialistsConfig) => {
  const groupMore = doctor?._tier === 'preferred' ? config.plan?.preferred[doctor._group]?.more : undefined;
  if (groupMore) return groupMore;
  if (config.plan && doctor?._tier === 'local-online')
    return {
      text: `مشاهده سایر پزشکان آنلاین ${config.expertiseName} ${config.cityName}`,
      url: `/s/${config.city}/${config.groupSlug}/?turn_type=consult`,
    };
  if (config.plan && doctor?._tier === 'national-online')
    return {
      text: `مشاهده سایر پزشکان آنلاین ${config.expertiseName}`,
      url: `/s/${config.plan.nationalOnline.route}/?turn_type=consult`,
    };
  return { text: `مشاهده سایر پزشکان ${config.expertiseName} ${config.cityName}`, url: searchPageUrl(config) };
};

/** click-doctor fields shared by both surfaces. */
const clickFields = (doctor: any, position: number, element?: string, config?: LocalPrivateSpecialistsConfig) => ({
  doctor: slugOf(doctor),
  position: position + 1,
  element,
  first_turn: firstTurnLabel(doctor),
  ...(doctor?._tier && { tier: doctor._tier }),
  ...(doctor?._mode && {
    mode: doctor._mode,
    line: suggestionLine(doctor, config),
    target: actionHref(doctor),
    ...(doctor._slot && { slot: doctor._slot }),
  }),
});

/**
 * Same-city specialists of the profile's specialty (config.ts decides which, and in what order) that
 * can be booked in person right now. Search gives the candidates (ranked, pre-filtered on its first-turn index);
 * each candidate is then confirmed with Saman — the same availability source the profile's own booking
 * card uses — and only `booking_available` doctors are kept, so nobody whose booking is closed or full
 * is ever suggested. Analytics: see events.ts.
 */
const useLocalPrivateSpecialists = (
  config: LocalPrivateSpecialistsConfig,
  ownSlug: string,
  placement: LocalSpecialistsPlacement,
) => {
  const customize = useCustomize(state => state.customize);
  const enabled = !customize.partnerKey;
  const route = `${config.city}/${config.groupSlug}`;
  const firstPage = useSearch({ route, query: { ...searchFilters(config), limit: 50 } }, { enabled });
  // Big cities: the specialty has more than one page of private offices (Isfahan dermatology: 124).
  const morePages = useQueries({
    queries: Array.from({ length: Math.max(0, (config.searchPages ?? 1) - 1) }, (_, index) => {
      const params = { route, query: { ...searchFilters(config), limit: 50, page: index + 2 } };
      return {
        queryKey: [ServerStateKeysEnum.Search, params],
        queryFn: () => search(params),
        enabled,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      };
    }),
  });
  // Plan mode: more city routes (preferred doctors of another specialty) and the national online route.
  // Optional sources — a failure there leaves that tier empty instead of hiding the suggestion.
  const plan = config.plan;
  const planQueries = useQueries({
    queries: [
      ...(plan?.extraRoutes ?? []).flatMap(extra =>
        Array.from({ length: extra.pages ?? 1 }, (_, index) => ({ route: extra.route, query: { limit: 50, page: index + 1 } })),
      ),
      ...(plan ? [{ route: plan.nationalOnline.route, query: { turn_type: 'consult', limit: 50 }, national: true }] : []),
    ].map(({ national, ...params }: any) => ({
      // Own key: the value is wrapped ({ national, data }), so it must not share the plain search cache.
      queryKey: [ServerStateKeysEnum.Search, 'local-specialists-plan', params],
      queryFn: async () => ({ national: !!national, data: await search(params) }),
      enabled,
      // The card waits for these to settle; one retry, not three, before an optional tier is dropped.
      retry: 1,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    })),
  });
  const pages = [firstPage, ...morePages];
  const searchData = {
    isSuccess: pages.every(page => page.isSuccess) && planQueries.every(page => page.isSuccess || page.isError),
    isError: pages.some(page => page.isError),
  };
  const results = useMemo(
    () => pages.flatMap(page => page.data?.search?.result ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages.map(page => page.dataUpdatedAt).join()],
  );
  const planResults = useMemo(
    () => ({
      city: planQueries.filter(page => !page.data?.national).flatMap(page => page.data?.data?.search?.result ?? []),
      national: planQueries.filter(page => page.data?.national).flatMap(page => page.data?.data?.search?.result ?? []),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planQueries.map(page => page.dataUpdatedAt).join()],
  );
  const planned = useMemo(
    () =>
      plan
        ? planCandidates([...results, ...planResults.city], planResults.national, { ...config, plan }, ownSlug)
        : null,
    [plan, results, planResults, config, ownSlug],
  );
  // Tiered mode checks clinic + online + office candidates; otherwise the in-person list only.
  const tiered = useMemo(
    () => (config.tiers && !plan ? tierCandidates(results, config, ownSlug) : null),
    [results, config, ownSlug, plan],
  );
  const candidates = useMemo(() => {
    const lists = planned
      ? [planned.preferred, planned.localOnline, planned.office, planned.nationalOnline]
      : tiered
        ? [tiered.clinic, tiered.online, tiered.office]
        : null;
    if (!lists) return rankLocalSpecialists(results, config, ownSlug).filter(hasIndexedInPersonTurn);
    const seen = new Set<string>();
    return lists.flat().filter(hit => {
      const slug = slugOf(hit);
      if (!slug || seen.has(slug)) return false;
      seen.add(slug);
      return true;
    });
  }, [planned, tiered, results, config, ownSlug]);
  // Same key as useSamanDoctorAvailabilityStatus, so a later visit to that profile reuses the answer.
  const availability = useQueries({
    queries: candidates.map(hit => {
      const slug = slugOf(hit);
      return {
        queryKey: [ServerStateKeysEnum.SamanDoctorAvailabilityStatus, slug],
        queryFn: () => samanDoctorAvailabilityStatus(slug),
        enabled: enabled && !!slug,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      };
    }),
  });

  const ready = searchData.isSuccess && availability.every(result => !result.isLoading || result.fetchStatus === 'idle');
  const doctors = useMemo(
    () => {
      if (planned && plan) {
        const samanBySlug = Object.fromEntries(candidates.map((hit, index) => [planSlug(hit), availability[index]?.data]));
        return assemblePlan(planned, samanBySlug, plan, config.includeExpertise);
      }
      if (tiered) {
        const samanBySlug = Object.fromEntries(candidates.map((hit, index) => [slugOf(hit), availability[index]?.data]));
        return assembleTiers(tiered, samanBySlug, config);
      }
      return uniqueByDoctorName(
        candidates.filter((_, index) => availability[index]?.data?.in_person_availability?.booking_available === true),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidates, availability.map(result => result.dataUpdatedAt).join()],
  );

  const track = (type: LocalSpecialistsEventType, fields?: Record<string, unknown>) =>
    splunkInstance('search').sendEvent(buildLocalSpecialistsEvent(type, { slug: ownSlug, placement, config }, fields));
  // Plan mode: candidates per tier before Saman, so «nobody bookable» can be told apart from «nobody listed».
  const planCandidateCounts = planned
    ? {
        preferred: planned.preferred.length,
        'local-online': planned.localOnline.length,
        office: planned.office.length,
        'national-online': planned.nationalOnline.length,
      }
    : undefined;

  // Nobody to suggest (or search failed): counted once, so views can be read against all openings.
  const emptySent = useRef(false);
  const searchFailed = enabled && searchData.isError;
  const noneBookable = enabled && ready && !doctors.length;
  useEffect(() => {
    if (emptySent.current || (!searchFailed && !noneBookable)) return;
    emptySent.current = true;
    track('empty', {
      reason: searchFailed ? 'search_error' : 'no_bookable',
      candidate_count: candidates.length,
      ...(planCandidateCounts && { candidate_tiers: planCandidateCounts }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFailed, noneBookable]);

  const hidden = !enabled || searchFailed || noneBookable;
  return { doctors, candidateCount: candidates.length, planCandidateCounts, isLoading: !ready, hidden, track };
};

/** `view`, once per mount, with the doctors actually on screen. */
const useViewEvent = (
  shown: any[] | undefined,
  extra: { bookable_count: number; candidate_count: number },
  track: (type: LocalSpecialistsEventType, fields?: Record<string, unknown>) => unknown,
  plan?: { all: any[]; candidateTiers?: Record<string, number>; config: LocalPrivateSpecialistsConfig; title?: string },
) => {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !shown?.length) return;
    sent.current = true;
    track('view', {
      shown_doctors: shown.map(slugOf),
      shown_count: shown.length,
      ...(shown.some(doctor => doctor?._tier) && { shown_tiers: shown.map(doctor => doctor?._tier ?? null) }),
      // Plan mode: everything needed to tell later what this visitor saw and why.
      ...(plan?.config.plan && {
        shown_modes: shown.map(doctor => doctor?._mode ?? null),
        shown_lines: shown.map(doctor => suggestionLine(doctor, plan.config)),
        ...(plan.title && { title: plan.title }),
        bookable_tiers: tierCounts(plan.all),
        candidate_tiers: plan.candidateTiers,
      }),
      ...extra,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
};

interface Props {
  config: LocalPrivateSpecialistsConfig;
  /** The profile's own doctor — never listed. */
  ownSlug: string;
}

/**
 * Booking «نوبت پر شده» modal: the same carousel the modal shows for online doctors (Recommend), with
 * this city's bookable private specialists and their first free turn right above «دریافت نوبت».
 */
const Recommend = ({ config, ownSlug }: Props) => {
  const { doctors, candidateCount, planCandidateCounts, isLoading, hidden, track } = useLocalPrivateSpecialists(
    config,
    ownSlug,
    'booking-full',
  );
  useViewEvent(isLoading ? undefined : doctors, { bookable_count: doctors.length, candidate_count: candidateCount }, track, {
    all: doctors,
    candidateTiers: planCandidateCounts,
    config,
    title: localPrivateSpecialistsTitle(config),
  });
  if (hidden) return null;

  const idOf = (doctor: any) => doctor.id ?? doctor.url;
  const handleClickDoctor = (id: string, element?: string) => {
    const position = doctors.findIndex(doctor => idOf(doctor) === id);
    if (position < 0) return;
    track('click-doctor', clickFields(doctors[position], position, element, config));
  };

  return (
    <div className="flex flex-col space-y-3" data-testid="local-private-specialists">
      <Text fontSize="sm" fontWeight="bold" className="leading-6">
        {localPrivateSpecialistsTitle(config)}
      </Text>
      {isLoading ? (
        <div className="flex pb-5 overflow-auto no-scroll space-s-3">
          <Skeleton w="15rem" h="13rem" rounded="lg" className="min-w-[15rem]" />
          <Skeleton w="15rem" h="13rem" rounded="lg" />
        </div>
      ) : (
        <RecommendCard
          listOfDoctors={doctors.map(doctor => ({
            image: doctor.image,
            displayExpertise: doctor.display_expertise,
            displayName: doctor.title ?? doctor.display_name,
            isBulk: doctor.is_bulk,
            ratesCount: doctor.rates_count,
            rate: doctor.satisfaction,
            url: doctor.url,
            id: idOf(doctor),
            action: [
              {
                title: isOnlineSuggestion(doctor) ? 'ویزیت آنلاین' : 'دریافت نوبت',
                description: suggestionLine(doctor, config),
                outline: false,
                href: actionHref(doctor),
              },
            ],
          }))}
          clickRecommendEvent={handleClickDoctor}
          showMore={{ text: `همه پزشکان ${config.expertiseName} ${config.cityName}`, url: searchPageUrl(config) }}
          onShowMoreClick={() => track('click-more', { target: searchPageUrl(config) })}
        />
      )}
    </div>
  );
};

/**
 * Profile, in its own card under the booking card: one doctor as the old substitute suggestion showed
 * it (banner + card + «مشاهده سایر …» link). `first-turn` order shows the earliest free turn; otherwise
 * one of the three best-rated bookable doctors. `placement="profile-open"` when the profile's own
 * booking is open (config.showWhenBookable).
 */
type SuggestionProps = Props & { placement?: Extract<LocalSpecialistsPlacement, 'profile' | 'profile-open'> };

const Suggestion = ({ config, ownSlug, placement = 'profile' }: SuggestionProps) => {
  const { doctors, candidateCount, planCandidateCounts, isLoading, hidden, track } = useLocalPrivateSpecialists(
    config,
    ownSlug,
    placement,
  );
  const position = useMemo(() => {
    if (isLoading || !doctors.length) return -1;
    return config.order === 'first-turn' ? 0 : random(0, Math.min(2, doctors.length - 1));
  }, [doctors, isLoading, config.order]);
  const doctor = position >= 0 ? doctors[position] : undefined;
  const shown = useMemo(() => (doctor ? [doctor] : undefined), [doctor]);
  const title = doctor ? suggestionTitle(doctor, config) : localPrivateSpecialistsTitle(config);
  const more = moreLink(doctor, config);
  useViewEvent(shown, { bookable_count: doctors.length, candidate_count: candidateCount }, track, {
    all: doctors,
    candidateTiers: planCandidateCounts,
    config,
    title,
  });
  const raviRate = useRaviCardRate(doctor ? slugOf(doctor) : undefined);
  if (hidden || !doctor) return null;

  const openDoctor = (element: string) => {
    track('click-doctor', clickFields(doctor, position, element, config));
    location.assign(element === 'button' ? actionHref(doctor) : doctor.url);
  };
  const openMore = () => {
    track('click-more', { target: more.url });
    location.assign(more.url);
  };

  return (
    <Card className="space-y-2 !rounded-none md:!rounded-lg" data-testid="local-private-specialists">
      <div className="px-3 py-2 text-white bg-green-600 border border-green-700 rounded-md">
        <Text className="text-sm font-bold leading-6">{title}</Text>
      </div>
      <div onClick={event => !(event.target as HTMLElement).closest('button') && openDoctor('card')}>
        <SearchCard
          avatarSize="lg"
          type="doctor"
          className="shadow-none !py-0 lg:!py-0 cursor-pointer"
          baseInfo={{
            displayName: doctor.title,
            expertise: doctor.display_expertise,
            avatar: doctor.image,
            isVerify: !doctor.is_bulk,
            rate: raviRate,
          }}
          actions={[
            {
              text: isOnlineSuggestion(doctor) ? `ویزیت آنلاین با دکتر ${doctor.title}` : `دریافت نوبت از دکتر ${doctor.title}`,
              outline: false,
              description: suggestionLine(doctor, config),
              action: () => openDoctor('button'),
            },
          ]}
        />
      </div>
      <Button block size="sm" className="text-xs opacity-70" variant="text" onClick={openMore}>
        {more.text}
      </Button>
    </Card>
  );
};

/**
 * Both surfaces behind an error boundary: an unexpected error in the suggestion hides only the
 * suggestion — never the profile's booking card or the booking page around it.
 */
export const LocalPrivateSpecialistsRecommend = (props: Props) => (
  <ErrorBoundary>
    <Recommend {...props} />
  </ErrorBoundary>
);

export const LocalPrivateSpecialistSuggestion = (props: SuggestionProps) => (
  <ErrorBoundary>
    <Suggestion {...props} />
  </ErrorBoundary>
);
